import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { toast } from 'sonner';

import { DeleteConfirmationDialog } from '@/components/DeleteConfirmationDialog';
import { TopNavbarActions } from '@/features/assistant/components/TopNavbarActions';
import { ROUTES } from '@/utils/routeUtils';

import { useDeleteSession } from '../hooks/useSessionsHistory';

interface AssistantSessionActionsProps {
  onNewSession: () => void;
}

export function AssistantSessionActions({ onNewSession }: AssistantSessionActionsProps) {
  const { t } = useTranslation();
  const { sessionId: urlSessionId } = useParams<{ sessionId?: string }>();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const deleteSessionMutation = useDeleteSession();

  const handleDeleteClick = useCallback(() => {
    if (!urlSessionId) {
      toast.error(t('assistant.delete.noSession'));
      return;
    }
    setShowDeleteDialog(true);
  }, [urlSessionId, t]);

  const handleConfirmDelete = useCallback(async () => {
    if (!urlSessionId) {
      setShowDeleteDialog(false);
      return;
    }
    try {
      await deleteSessionMutation.mutateAsync(urlSessionId);
      toast.success(t('assistant.delete.success'));
      onNewSession();
    } catch (error) {
      console.error('Failed to delete session:', error);
      toast.error(t('assistant.delete.failed'), {
        description: error instanceof Error ? error.message : t('assistant.chat.unexpectedError'),
      });
    } finally {
      setShowDeleteDialog(false);
    }
  }, [urlSessionId, deleteSessionMutation, onNewSession, t]);

  return (
    <>
      <TopNavbarActions
        currentSessionId={urlSessionId ?? null}
        historyRoute={ROUTES.HISTORY}
        isDeleting={deleteSessionMutation.isPending}
        onDeleteSession={handleDeleteClick}
        onNewSession={onNewSession}
      />
      <DeleteConfirmationDialog
        cancelLabel={t('assistant.delete.cancel')}
        confirmLabel={t('assistant.delete.confirm')}
        description={t('assistant.delete.confirmDescription')}
        isPending={deleteSessionMutation.isPending}
        onConfirm={handleConfirmDelete}
        onOpenChange={setShowDeleteDialog}
        open={showDeleteDialog}
        title={t('assistant.delete.title')}
      />
    </>
  );
}
